import { useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

// Business API Types
export interface Business {
  id: string;
  owner_id: string;
  category_id: string | null;
  category_name: string | null;
  other_category_name: string | null;
  name: string;
  description: string | null;
  logo_url: string | null;
  email: string | null;
  phone_number: string | null;
  phone_country_code: string | null;
  street_address: string | null;
  city: string | null;
  state_name: string | null;
  postal_code: string | null;
  country_name: string | null;
  latitude: number | null;
  longitude: number | null;
  same_as_business: boolean;
  billing_name: string | null;
  gstin: string | null;
  billing_email: string | null;
  billing_phone: string | null;
  billing_country_code: string | null;
  billing_street_address: string | null;
  billing_city: string | null;
  billing_state_name: string | null;
  billing_postal_code: string | null;
  billing_country_name: string | null;
  billing_latitude: number | null;
  billing_longitude: number | null;
  status: 'pending' | 'active' | 'inactive' | 'suspended';
  is_verified: boolean;
  average_rating: number;
  total_ratings: number;
  created_at: string;
}

export interface CreateBusinessRequest {
  // Owner details
  full_name?: string; // Will be stored as user's display_name

  // Required fields
  business_name: string;
  business_category: string; // UUID of service category
  business_email: string;
  phone_number: string;
  street_address: string;
  city: string;
  state_name: string;
  postal_code: string;
  country_name: string;
  gst_number?: string;

  // Optional fields
  other_category_name?: string; // Required if category is "Other"
  phone_country_code?: string; // e.g., "IN"
  phone_dial_code?: string; // e.g., "+91"
  latitude?: number;
  longitude?: number;

  // Billing details
  same_as_business?: boolean; // If true, copies business address to billing
  registered_business_name?: string;
  billing_email?: string;
  billing_phone_number?: string;
  billing_phone_country_code?: string;
  billing_phone_dial_code?: string;
  billing_street_address?: string;
  billing_city?: string;
  billing_state_name?: string;
  billing_postal_code?: string;
  billing_country_name?: string;
  billing_latitude?: number;
  billing_longitude?: number;
}

interface CreateBusinessResponse {
  success: boolean;
  message: string;
  data: {
    business: Business;
  };
}

interface GetBusinessResponse {
  success: boolean;
  message: string;
  data: Business;
}

export interface UpdateBusinessRequest {
  // Owner details
  full_name?: string; // Will be stored as user's display_name

  // Business fields (all optional for update)
  business_name?: string;
  business_category?: string;
  other_category_name?: string;
  business_email?: string;
  phone_number?: string;
  phone_country_code?: string;
  phone_dial_code?: string;
  street_address?: string;
  city?: string;
  state_name?: string;
  postal_code?: string;
  country_name?: string;
  latitude?: number | null;
  longitude?: number | null;
  gst_number?: string;

  // Billing details
  same_as_business?: boolean;
  registered_business_name?: string;
  billing_email?: string;
  billing_phone_number?: string;
  billing_phone_country_code?: string;
  billing_phone_dial_code?: string;
  billing_street_address?: string;
  billing_city?: string;
  billing_state_name?: string;
  billing_postal_code?: string;
  billing_country_name?: string;
  billing_latitude?: number | null;
  billing_longitude?: number | null;
}

interface UpdateBusinessResponse {
  success: boolean;
  message: string;
  data: {
    business: Business;
  };
}

interface UseBusinessReturn {
  isLoading: boolean;
  error: string | null;
  createBusiness: (data: CreateBusinessRequest) => Promise<Business>;
  updateBusiness: (data: UpdateBusinessRequest) => Promise<Business>;
  getMyBusiness: () => Promise<Business | null>;
}

export const useBusiness = (): UseBusinessReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const createBusiness = useCallback(
    async (data: CreateBusinessRequest): Promise<Business> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.post<CreateBusinessResponse>(API_ENDPOINTS.CREATE_BUSINESS, data);

        return response.data.data.business;
      } catch (err: any) {
        const message =
          err?.response?.data?.message || err.message || 'Failed to create business';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const updateBusiness = useCallback(
    async (data: UpdateBusinessRequest): Promise<Business> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.put<UpdateBusinessResponse>(API_ENDPOINTS.UPDATE_BUSINESS, data);

        return response.data.data.business;
      } catch (err: any) {
        const message =
          err?.response?.data?.message || err.message || 'Failed to update business';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const getMyBusiness = useCallback(async (): Promise<Business | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get<GetBusinessResponse>(API_ENDPOINTS.GET_MY_BUSINESS);

      return response.data.data;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return null;
      }
      const message =
        err?.response?.data?.message || err.message || 'Failed to get business';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    error,
    createBusiness,
    updateBusiness,
    getMyBusiness,
  };
};

export default useBusiness;
